/**
 * usePragmaticContextAware.ts - Context-aware drag and drop hook
 * 
 * ARCHITECTURE:
 * - Combines draggable and droppable with context detection
 * - Supports different behaviors based on source/target context
 * - Common pattern: same-context = reorder, different-context = link/copy/download
 * 
 * EXAMPLES:
 * - Same list = reorder items
 * - Different list = create link
 * - File to filesystem = download
 * - Field to field = create mapping
 */

import { useRef, RefObject } from 'react';
import { usePragmaticDraggable } from './usePragmaticDraggable';
import { usePragmaticDropTarget } from './usePragmaticDropTarget';

export interface ContextAwareConfig<TData = any> {
  /** Unique identifier for this context (e.g., 'list-1', 'dataset-A', 'folder-id') */
  contextId: string;
  
  /** Type of draggable (e.g., 'item', 'field', 'file') */
  type: string;
  
  /** Data to attach to drag operation */
  data?: TData;
  
  /** Function to get initial data when drag starts */
  getInitialData?: () => TData;
  
  /** Array of types this drop target accepts */
  accepts?: string[];
  
  /** Callback when drag starts */
  onDragStart?: (data: TData) => void;
  
  /** Callback when drag ends */
  onDragEnd?: (data: TData) => void;
  
  /** 
   * Callback for same-context drops (e.g., reorder within same list)
   * @param sourceData - Data from the dragged item
   * @param targetData - Data from the drop target
   */
  onSameContextDrop?: (sourceData: any, targetData: TData) => void;
  
  /** 
   * Callback for different-context drops (e.g., link between lists, download file)
   * @param sourceData - Data from the dragged item
   * @param targetData - Data from the drop target
   */
  onDifferentContextDrop?: (sourceData: any, targetData: TData) => void;
  
  /** Callback when draggable enters this drop target */
  onDragEnter?: () => void;
  
  /** Callback when draggable leaves this drop target */
  onDragLeave?: () => void;
  
  /** Custom function to determine if drop is allowed */
  canDrop?: (sourceData: any, targetData: TData) => boolean;
}

/**
 * Hook for context-aware drag and drop
 * 
 * Automatically detects whether source and target are in the same context
 * and routes to appropriate callback.
 * 
 * @example
 * ```tsx
 * // Reorderable list with cross-list linking
 * const ref = useRef<HTMLDivElement>(null);
 * 
 * usePragmaticContextAware(ref, {
 *   contextId: 'list-1',
 *   type: 'item',
 *   data: { id: '123', index: 0 },
 *   
 *   // Same list - reorder
 *   onSameContextDrop: (source, target) => {
 *     reorderItems(source.index, target.index);
 *   },
 *   
 *   // Different list - create link
 *   onDifferentContextDrop: (source, target) => {
 *     createLink(source.id, target.id);
 *   }
 * });
 * ```
 * 
 * @example
 * ```tsx
 * // File download on external drop
 * usePragmaticContextAware(ref, {
 *   contextId: 'file-viewer',
 *   type: 'file',
 *   data: { fileId: '123', url: 'https://...' },
 *   
 *   // Same context - reorder files
 *   onSameContextDrop: (source, target) => {
 *     reorderFiles(source.fileId, target.fileId);
 *   },
 *   
 *   // Different context - download file
 *   onDifferentContextDrop: (source, target) => {
 *     downloadFile(source.url, source.filename);
 *   }
 * });
 * ```
 */
export function usePragmaticContextAware<T extends HTMLElement, TData = any>(
  elementRef: RefObject<T>,
  config: ContextAwareConfig<TData>
): void {
  const configRef = useRef(config);
  configRef.current = config;

  // Make element draggable
  usePragmaticDraggable(elementRef, {
    type: config.type,
    data: {
      contextId: config.contextId,
      ...(config.data || {}),
    },
    getInitialData: config.getInitialData ? () => ({
      contextId: configRef.current.contextId,
      ...(configRef.current.getInitialData?.() || {})
    }) : undefined,
    onDragStart: config.onDragStart,
    onDrop: config.onDragEnd,
  });

  // Make element a drop target
  usePragmaticDropTarget(elementRef, {
    accepts: config.accepts || [config.type],
    onDragEnter: config.onDragEnter,
    onDragLeave: config.onDragLeave,
    canDrop: config.canDrop ? ({ source }) => {
      return configRef.current.canDrop!(source.data, configRef.current.data as TData);
    } : undefined,
    onDrop: ({ source }) => {
      const sourceContextId = source.data.contextId as string;
      const targetContextId = configRef.current.contextId;
      const sourceData = source.data;
      const targetData = configRef.current.data as TData;

      // Same context - use same-context handler
      if (sourceContextId === targetContextId && configRef.current.onSameContextDrop) {
        configRef.current.onSameContextDrop(sourceData, targetData);
      }
      // Different context - use different-context handler
      else if (sourceContextId !== targetContextId && configRef.current.onDifferentContextDrop) {
        configRef.current.onDifferentContextDrop(sourceData, targetData);
      }
    },
  });
}
